import { useContext } from 'react';
import { useDisclosure } from '@ds';
import { AuthContext } from '@lp/core/auth';
import { Case, CaseStatus, ServiceType } from '@/api/apis/case';
import { useUpdateCaseStatus } from '@/hooks/useUpdateCaseStatus';
import { useProceedToNextCaseStatus } from '@/hooks/useProceedToNextCaseStatus';
import { useProceedBlock } from '@/hooks/useProceedBlock';
import { CaseDetailsPermissions } from '@/permissions';
import { getActionsForStatus, getCaseWasDeclined, getTargetProceedStatus } from '@/utils/helpers';

export const useCaseStatusMenuState = (caseData: Case) => {
  const { caseId, outcomeData, status, clinicalData, serviceType, randomization, reAllocationCaseId, organType } =
    caseData;

  const { checkPermission } = useContext(AuthContext);
  const hasPermission = checkPermission(CaseDetailsPermissions.CaseStatusUpdate);

  const { updateStatus, isSubmitting } = useUpdateCaseStatus();

  const declineCase = useDisclosure();
  const declineService = useDisclosure();

  const isDirectAcquisitionCase = serviceType === ServiceType.DirectAcquisition;
  const targetProceedStatus = getTargetProceedStatus(serviceType, status);
  const caseWasDeclined = getCaseWasDeclined(outcomeData);

  const {
    nextStatus,
    prevStatus,
    canMoveToPrevStatus,
    canMoveToNextStatus,
    canChangeStatus,
    canDeclineCase,
    canDeclineService,
  } = getActionsForStatus(status, caseWasDeclined);

  const { onOcs, offOcs, onProceedStatusClick, isReallocationDataTransferPending } = useProceedToNextCaseStatus({
    randomization,
    nextStatus,
    caseId,
    updateStatus,
    clinicalData,
    reAllocationCaseId,
    organType,
  });

  const isProceeding = isSubmitting || isReallocationDataTransferPending;

  const { proceedDisabled, proceedInteractionDisabled, actionHintMessage } = useProceedBlock({
    caseData,
    nextStatus,
    targetProceedStatus,
    canMoveToNextStatus,
    isProceeding,
  });

  const proceedHidden = !canMoveToNextStatus || (isDirectAcquisitionCase && status !== CaseStatus.ClinicalScreening);

  const disabledKeys = isProceeding ? ['proceedStatus', 'rollBackStatus', 'declineCase', 'declineService'] : [];

  const onProceedClick = () => {
    if (proceedInteractionDisabled) return;
    onProceedStatusClick(targetProceedStatus);
  };

  return {
    hasPermission,
    isDirectAcquisitionCase,
    proceedHidden,
    nextStatus,
    prevStatus,
    canMoveToPrevStatus,
    canChangeStatus,
    canDeclineCase,
    canDeclineService,
    isProceeding,
    proceedDisabled,
    proceedInteractionDisabled,
    disabledKeys,
    actionHintMessage,
    updateStatus,
    onProceedClick,
    onOcs,
    offOcs,
    declineCase,
    declineService,
  };
};
